import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Download, Calendar, Clock, User, Users,
  XCircle, Lightbulb, Info, ChevronRight, Loader2, FileText,
  ThumbsUp, ThumbsDown
} from 'lucide-react';

const SUGGESTION_CONFIG = {
  error_dato: { icon: XCircle, color: '#ef4444', label: 'Error de Datos' },
  oportunidad: { icon: Lightbulb, color: '#f59e0b', label: 'Oportunidad' },
  consejo: { icon: Info, color: '#3b82f6', label: 'Consejo' },
};

export default function SessionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSession();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSession = async () => {
    setLoading(true);
    try {
      const data = await api.get(`/api/sessions/${id}`);
      setSession(data);
    } catch (err) {
      toast.error('Error al cargar sesión');
      navigate('/sessions');
    }
    setLoading(false);
  };

  const exportSession = async () => {
    try {
      const data = await api.get(`/api/sessions/${id}/export`);
      const blob = new Blob([data.content], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Exportación Markdown descargada');
    } catch (err) {
      toast.error('Error al exportar');
    }
  };

  const exportPdf = async () => {
    try {
      const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${API_BASE}/api/sessions/${id}/export-pdf`);
      if (!res.ok) throw new Error('Error generando PDF');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sesion-${id.substring(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch (err) {
      toast.error('Error al exportar PDF: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 size={24} className="animate-spin" style={{ color: 'var(--accent-primary)' }} />
      </div>
    );
  }

  if (!session) return null;

  const transcript = session.transcript_full || [];
  const suggestions = session.suggestions || [];

  return (
    <div className="p-6 lg:p-8 max-w-6xl" data-testid="session-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/sessions')}
            data-testid="back-to-sessions"
            className="p-2 rounded-lg transition-colors"
            style={{ backgroundColor: 'var(--surface-secondary)', color: 'var(--text-secondary)' }}
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="font-heading text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
              Sesión: {session.agent_name || 'Agente'}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-muted)' }}>
                <Calendar size={11} />
                {new Date(session.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                  session.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                }`}
              >
                {session.status === 'active' ? 'Activa' : 'Completada'}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportPdf}
            data-testid="export-pdf-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all"
            style={{ backgroundColor: 'var(--accent-primary)', color: '#fff' }}
          >
            <FileText size={16} />
            PDF
          </button>
          <button
            onClick={exportSession}
            data-testid="export-session-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all"
            style={{ backgroundColor: 'var(--surface-secondary)', color: 'var(--text-secondary)' }}
          >
            <Download size={16} />
            Markdown
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Entradas" value={transcript.length} color="var(--accent-primary)" />
        <StatCard
          label="Errores"
          value={suggestions.filter(s => s.type === 'error_dato').length}
          color="#ef4444"
        />
        <StatCard
          label="Oportunidades"
          value={suggestions.filter(s => s.type === 'oportunidad').length}
          color="#f59e0b"
        />
        <StatCard
          label="Consejos"
          value={suggestions.filter(s => s.type === 'consejo').length}
          color="#3b82f6"
        />
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Transcript */}
        <div className="lg:col-span-3">
          <h2 className="font-heading font-semibold text-base mb-4" style={{ color: 'var(--text-primary)' }}>
            Transcripción Completa
          </h2>
          <div
            className="rounded-xl border p-5 space-y-3 max-h-[600px] overflow-y-auto"
            style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}
            data-testid="session-transcript"
          >
            {transcript.map((entry, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{
                    backgroundColor: entry.speaker === 'Agente' ? 'var(--accent-primary)' : 'var(--surface-secondary)',
                  }}
                >
                  {entry.speaker === 'Agente' ? (
                    <User size={12} className="text-white" />
                  ) : (
                    <Users size={12} style={{ color: 'var(--text-muted)' }} />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-semibold" style={{ color: entry.speaker === 'Agente' ? 'var(--accent-primary)' : 'var(--text-muted)' }}>
                      {entry.speaker}
                    </span>
                    {entry.timestamp && (
                      <span className="text-[10px] font-mono" style={{ color: 'rgba(148,163,184,0.5)' }}>
                        {new Date(entry.timestamp).toLocaleTimeString('es-ES')}
                      </span>
                    )}
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                    {entry.text}
                  </p>
                </div>
              </div>
            ))}
            {transcript.length === 0 && (
              <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>
                Sin transcripción disponible
              </p>
            )}
          </div>
        </div>

        {/* Suggestions Timeline */}
        <div className="lg:col-span-2">
          <h2 className="font-heading font-semibold text-base mb-4" style={{ color: 'var(--text-primary)' }}>
            Sugerencias ({suggestions.length})
          </h2>
          <div className="space-y-3 max-h-[600px] overflow-y-auto" data-testid="session-suggestions">
            {suggestions.map((s, idx) => {
              const config = SUGGESTION_CONFIG[s.type] || SUGGESTION_CONFIG.consejo;
              const Icon = config.icon;
              return (
                <div
                  key={s.id || idx}
                  className="border-l-4 rounded-r-lg p-4 border"
                  style={{
                    borderLeftColor: config.color,
                    backgroundColor: 'var(--surface-primary)',
                    borderColor: 'var(--border-color)',
                  }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon size={14} style={{ color: config.color }} />
                    <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: config.color }}>
                      {config.label}
                    </span>
                    <span
                      className="text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase ml-auto"
                      style={{
                        backgroundColor: s.urgency === 'high' ? 'rgba(239,68,68,0.15)' : s.urgency === 'medium' ? 'rgba(245,158,11,0.15)' : 'rgba(59,130,246,0.15)',
                        color: s.urgency === 'high' ? '#ef4444' : s.urgency === 'medium' ? '#f59e0b' : '#3b82f6',
                      }}
                    >
                      {s.urgency}
                    </span>
                  </div>
                  <h4 className="font-heading font-bold text-xs mb-1" style={{ color: 'var(--text-primary)' }}>
                    {s.title}
                  </h4>
                  <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                    {s.body}
                  </p>
                  {s.product_ref && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono mt-2 px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--surface-secondary)', color: 'var(--accent-primary)' }}>
                      <ChevronRight size={8} />
                      {s.product_ref}
                    </span>
                  )}
                  {s.feedback && (
                    <div className="flex items-center gap-1 mt-2 text-[10px] font-medium">
                      {s.feedback === 'accepted' ? (
                        <span className="flex items-center gap-1 text-green-400"><ThumbsUp size={10} /> Aceptada</span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-400"><ThumbsDown size={10} /> Descartada</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {suggestions.length === 0 && (
              <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>
                Sin sugerencias
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div
      className="rounded-xl border p-4"
      style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}
    >
      <p className="text-2xl font-heading font-bold" style={{ color }}>{value}</p>
      <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{label}</p>
    </div>
  );
}
